import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Translate } from 'react-localize-redux';

interface InpatientCurrentSituationSectionProps {
  data: any;
}

const MetricCard = ({ label, value }: { label: React.ReactNode; value: string | number }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
  </Paper>
);

const InpatientCurrentSituationSection = ({ data }: InpatientCurrentSituationSectionProps) => {
  const stats = data?.stats;
  const totals = stats?.totals;
  const rows = stats?.byDepartment || [];

  if (!totals) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">
          <Translate id="hospital.analytics.inpatient_current.section_title" />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.inpatient_current.section_note" />
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_current.current_patients" />}
            value={totals.currentPatients}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_current.beds_occupied_capacity" />}
            value={`${totals.occupiedBeds} / ${totals.totalCapacity}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_current.occupancy_rate" />}
            value={`${totals.occupancyRate}%`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_current.average_current_los" />}
            value={totals.averageCurrentLosDays}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_current.patients_over_30" />}
            value={totals.patientsOver30Days}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Translate id="hospital.analytics.inpatient_current.service" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.current_patients" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.occupied_beds" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.available_beds" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.total_capacity" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.occupancy_rate" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.average_current_los" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.patients_over_7" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.patients_over_14" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_current.patients_over_30" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any) => (
              <TableRow key={row.departmentUuid}>
                <TableCell>{row.departmentName}</TableCell>
                <TableCell align="right">{row.currentPatients}</TableCell>
                <TableCell align="right">{row.occupiedBeds}</TableCell>
                <TableCell align="right">{row.availableBeds}</TableCell>
                <TableCell align="right">{row.totalCapacity}</TableCell>
                <TableCell align="right">{row.occupancyRate}%</TableCell>
                <TableCell align="right">{row.averageCurrentLosDays}</TableCell>
                <TableCell align="right">{row.patientsOver7Days}</TableCell>
                <TableCell align="right">{row.patientsOver14Days}</TableCell>
                <TableCell align="right">{row.patientsOver30Days}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell><strong><Translate id="hospital.analytics.inpatient_current.total_row" /></strong></TableCell>
              <TableCell align="right"><strong>{totals.currentPatients}</strong></TableCell>
              <TableCell align="right"><strong>{totals.occupiedBeds}</strong></TableCell>
              <TableCell align="right"><strong>{Math.max(totals.totalCapacity - totals.occupiedBeds, 0)}</strong></TableCell>
              <TableCell align="right"><strong>{totals.totalCapacity}</strong></TableCell>
              <TableCell align="right"><strong>{totals.occupancyRate}%</strong></TableCell>
              <TableCell align="right"><strong>{totals.averageCurrentLosDays}</strong></TableCell>
              <TableCell align="right" colSpan={3} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InpatientCurrentSituationSection;
